import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRealtime, useRealtimeStatus } from '../utils/realtime';
import { Wifi, WifiOff, Loader2, Users } from 'lucide-react';

const STATUS_DOT = {
  online: 'bg-[var(--p-3d9a55)]',
  connecting: 'bg-amber-400 animate-pulse',
  offline: 'bg-[var(--p-f0592b)]',
};

const STATUS_LABEL = {
  online: 'En ligne',
  connecting: 'Connexion…',
  offline: 'Hors ligne',
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] + (parts[1]?.[0] || '')).toUpperCase().slice(0, 2);
};

const colorFromName = (name) => {
  const colors = ['bg-[var(--brand)]', 'bg-blue-500', 'bg-[var(--p-3d9a55)]', 'bg-orange-500', 'bg-pink-500', 'bg-[var(--brand)]'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h + name.charCodeAt(i)) % colors.length;
  return colors[h];
};

export const PresenceIndicator = ({ testId = 'presence-indicator' }) => {
  const status = useRealtimeStatus();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);

  useRealtime(['presence.updated'], (payload) => {
    setUsers(Array.isArray(payload.users) ? payload.users : []);
  });

  // Close popup when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-presence-popup]') && !e.target.closest('[data-presence-trigger]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const total = users.length;

  // V6.0.86 — Apple HIG polish: when alone online, just show a discreet status dot (no "1 👤" chip noise)
  if (status === 'online' && total <= 1 && !open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-presence-trigger
        data-testid={testId}
        className="hdr-btn"
        data-keepicon="1"
        aria-label="En ligne · vous êtes la seule personne connectée"
        title="En ligne · vous êtes la seule personne connectée. Clic : qui est connecté."
      >
        <span className="w-2 h-2 rounded-full bg-[var(--p-3d9a55)]" />
        <span className="hdr-lbl">En ligne</span>
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-presence-trigger
        data-testid={testId}
        className="hdr-btn"
        data-keepicon="1"
        aria-label={`${STATUS_LABEL[status]} — ${total} en ligne`}
        title={`${total} personne${total > 1 ? 's' : ''} connectée${total > 1 ? 's' : ''} en ce moment`}
      >
        <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
        {status === 'online' ? (
          <>
            <Users size={14} className="text-[var(--texte-2)]" />
            <span className="hdr-lbl">{total} en ligne</span>
          </>
        ) : status === 'connecting' ? (
          <>
            <Loader2 size={11} className="animate-spin text-[var(--texte-2)]" />
            <span className="text-[var(--texte-2)]">…</span>
          </>
        ) : (
          <>
            <WifiOff size={11} className="text-[var(--p-c43c12)]" />
            <span className="text-[var(--p-c43c12)] font-medium">Hors ligne</span>
          </>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            data-presence-popup
            className="absolute right-0 mt-2 w-64 bg-[var(--carte)] rounded-2xl shadow-xl border border-[var(--bord)] p-3 z-50"
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-xs font-semibold text-[var(--texte-2)] uppercase tracking-wide">
                {status === 'online' ? 'En ligne' : STATUS_LABEL[status]}
              </p>
              <span className="text-xs font-semibold text-[var(--texte)]">{total}</span>
            </div>

            {status !== 'online' && (
              <div className="text-sm text-[var(--texte-2)] px-1 py-2 flex items-center gap-2">
                <WifiOff size={14} />
                {status === 'connecting'
                  ? 'Connexion en cours…'
                  : 'Pas de connexion temps réel — vos modifs sont sauvegardées mais ne se diffusent pas en direct.'}
              </div>
            )}

            {status === 'online' && total === 0 && (
              <p className="text-sm text-[var(--texte-2)] px-1 py-2">Personne d'autre n'est connecté.</p>
            )}

            {status === 'online' && total > 0 && (
              <div className="space-y-1.5">
                {users.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-2.5 px-1 py-1">
                    <div className="relative">
                      {u.picture ? (
                        <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className={`w-8 h-8 rounded-full ${colorFromName(u.name || '')} flex items-center justify-center text-[var(--sur-couleur)] font-semibold text-xs`}>
                          {initials(u.name)}
                        </div>
                      )}
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[var(--p-3d9a55)] border-2 border-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--texte)] truncate">{u.name || 'Utilisateur'}</p>
                      {u.role && (
                        <p className="text-xs text-[var(--texte-2)] capitalize truncate">{u.role}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PresenceIndicator;
